/**
 * 需求 R117：分頁「輸入頁碼跳頁」的純邏輯層（不含 React，方便 tests/ 直接 require 驗證）。
 *
 * PM 2026-08-18 逐字拍板：
 *   Q1 互動形式        = B  只有輸入框、按 Enter 跳頁（不做「前往」按鈕）
 *   Q2 擺放位置        = A  接在「下一頁」按鈕右側（同一列最右）
 *   Q3 文案／跳頁後行為 = 文案照「第 [__] 頁 / 共 170 頁　前往」、跳完 A 清空輸入框
 *   Q4 無效輸入        = B  跳提示「請輸入 1–170 之間的頁碼」且不跳頁
 *   Q5 施作範圍        = A  四頁全做（產品／型錄／客戶／報價單）
 *   Q6 單頁時          = A  總頁數只有 1 頁時跳頁框一併隱藏
 *   Q7 順手修既有缺陷   = A  兩個都修（客戶管理頁碼補省略號、型錄管理補「每頁顯示」選單）
 *   Q8 實作方式        = A  抽成共用元件，四頁 import
 *
 * Q4=B 的關鍵是「不跳頁」：任何無效輸入都不得讓呼叫端送出 page<1 或 page>totalPages 的請求，
 * 所以驗證一律在這裡收斂成單一出口 parsePageInput()，四頁不各自寫一套判斷。
 */

/** Q4=B：只接受純十進位整數字串。小數（2.5）、負數（-1）、非數字（abc）、空白一律不合格。 */
const INTEGER_ONLY = /^\d+$/;

/**
 * 解析使用者輸入的頁碼。
 *
 * @param {string} raw        輸入框的原始字串（未 trim）
 * @param {number} totalPages 總頁數
 * @returns {{ valid: boolean, page: number|null, message: string }}
 *          valid=true 時 page 必為 1..totalPages 的整數；
 *          valid=false 時 page 一律為 null，message 為要跳出的提示文字（Q4=B）。
 */
function parsePageInput(raw, totalPages) {
  const max = Number(totalPages);
  // 總頁數本身不合法（NaN／0／負數／非整數）時，沒有任何頁碼可以跳 → 一律不跳頁。
  if (!Number.isInteger(max) || max < 1) {
    return { valid: false, page: null, message: invalidMessage(max) };
  }

  const text = String(raw == null ? '' : raw).trim();
  if (!INTEGER_ONLY.test(text)) {
    // 空字串、空白、小數、負數、含正負號、全形數字、英文字母都會落在這裡。
    return { valid: false, page: null, message: invalidMessage(max) };
  }

  const page = Number(text);
  if (!Number.isSafeInteger(page) || page < 1 || page > max) {
    // 0、超過總頁數、以及長到失去精度的數字串。
    return { valid: false, page: null, message: invalidMessage(max) };
  }

  return { valid: true, page, message: '' };
}

/**
 * Q4=B 的提示文字。破折號用的是 en dash（–），與 PM 留言逐字一致：
 *   「請輸入 1–170 之間的頁碼」
 */
function invalidMessage(totalPages) {
  const max = Number.isInteger(Number(totalPages)) && Number(totalPages) >= 1 ? Number(totalPages) : 1;
  return `請輸入 1–${max} 之間的頁碼`;
}

/**
 * Q7=A(a)：客戶管理原本是 `Array.from({ length: totalPages })` 把每一頁的頁碼全部渲染出來，
 * 客戶數長大之後整列會爆版。這裡提供與型錄管理（CatalogPage.getPageNumbers）相同的視窗演算法：
 *   總頁數 ≤ maxFull 時全部列出；否則 1 … 目前頁±1 … 末頁。
 * 回傳陣列中的 '...' 代表省略號（呼叫端自行決定要渲染成什麼）。
 */
function getPageNumbers(page, totalPages, maxFull = 7) {
  const max = Number(totalPages);
  if (!Number.isInteger(max) || max < 1) return [];
  const current = Math.min(Math.max(Number(page) || 1, 1), max);

  if (max <= maxFull) {
    return Array.from({ length: max }, (_, i) => i + 1);
  }

  const pages = [1];
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(max - 1, current + 1); i++) pages.push(i);
  if (current < max - 2) pages.push('...');
  pages.push(max);
  return pages;
}

export { parsePageInput, invalidMessage, getPageNumbers, INTEGER_ONLY };
